// pages/Agents/AgentSpace/AgentSpaceAgentDetail.js
// Agent detail page (workflows view).
//
// Sections (top → bottom):
//   1. Header card: back + agent icon/tagline + status chip + Open Chat + Settings
//   2. Stat strip: 3 metric cards with accent rails + trend chips
//   3. Workflows panel:
//      - Title + segmented filter (All / In Progress / Completed)
//      - Inline search with soft chip styling
//      - List of workflow rows (richer than a table — status rail, title, step
//        badges, summary preview, action chips, timestamp, chevron)
//      - Empty / loading / load-more states
// Clicking a workflow row opens the right-side drawer with full step detail.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Skeleton,
    Tooltip,
    Chip,
    Drawer,
    Button,
    InputBase, // Added here
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PollIcon from '@mui/icons-material/Poll';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import EventIcon from '@mui/icons-material/Event';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import axiosInstance from '../../../../utils/axiosInstance';
import AgentSpaceTaskDetail from './AgentSpaceTaskDetail';
import { useAgentSpace } from '../../../../contexts/AgentSpaceContext';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#8b5cf6',
    success: '#059669',
    danger: '#dc2626',
    warning: '#f59e0b',
    info: '#3b82f6',
};

const ICON_MAP = {
    PollIcon: (s) => <PollIcon sx={{ fontSize: s }} />,
    PeopleIcon: (s) => <PeopleIcon sx={{ fontSize: s }} />,
    ChatIcon: (s) => <ChatIcon sx={{ fontSize: s }} />,
    EventIcon: (s) => <EventIcon sx={{ fontSize: s }} />,
    MailOutlineIcon: (s) => <MailOutlineIcon sx={{ fontSize: s }} />,
    EmailIcon: (s) => <EmailIcon sx={{ fontSize: s }} />,
    CalendarMonthIcon: (s) => <CalendarMonthIcon sx={{ fontSize: s }} />,
    CleaningServicesIcon: (s) => <CleaningServicesIcon sx={{ fontSize: s }} />,
};

function getIcon(name, size = 20) {
    const fn = ICON_MAP[name];
    return fn ? fn(size) : <AutoAwesomeIcon sx={{ fontSize: size }} />;
}

function formatRelative(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    const hr = Math.floor(diff / 3600000);
    const day = Math.floor(diff / 86400000);
    if (min < 1) return 'Just now';
    if (min < 60) return `${min}m ago`;
    if (hr < 24) return `${hr}h ago`;
    if (day < 7) return `${day}d ago`;
    return d.toLocaleDateString();
}

// One stat card with a colored accent rail + icon chip + headline number.
function StatCard({ icon, label, value, suffix, color, helper }) {
    return (
        <Box sx={{
            flex: 1, minWidth: 0,
            position: 'relative',
            p: { xs: 1.5, sm: 1.85 },
            borderRadius: '14px',
            bgcolor: '#fff',
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
            transition: 'all 0.18s ease',
            '&:hover': { boxShadow: `0 4px 14px ${color}22`, borderColor: `${color}30`, transform: 'translateY(-1px)' },
            // Left accent rail in the metric's color.
            '&::before': {
                content: '""', position: 'absolute', left: 0, top: 12, bottom: 12,
                width: 3, borderRadius: '0 3px 3px 0', bgcolor: color,
            },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, mb: 0.5 }}>
                <Box sx={{
                    width: 26, height: 26, borderRadius: '8px',
                    bgcolor: `${color}14`, color, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {icon}
                </Box>
                <Typography sx={{
                    fontFamily, fontSize: '0.66rem', fontWeight: 700,
                    color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>
                    {label}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{
                    fontFamily, fontSize: { xs: '1.45rem', sm: '1.65rem' }, fontWeight: 800,
                    color: colors.textPrimary, lineHeight: 1.05,
                }}>
                    {value}
                </Typography>
                {suffix && (
                    <Typography sx={{ fontFamily, fontSize: '0.72rem', fontWeight: 600, color: colors.textTertiary }}>
                        {suffix}
                    </Typography>
                )}
            </Box>
            {helper && (
                <Typography sx={{ fontFamily, fontSize: '0.65rem', color: colors.textTertiary, mt: 0.35 }} noWrap>
                    {helper}
                </Typography>
            )}
        </Box>
    );
}

// Pill (segmented) — used for All / In Progress / Completed filter.
function PillTab({ active, label, count, onClick, accent }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.6,
                px: 1.4, py: 0.6,
                borderRadius: '8px',
                cursor: 'pointer',
                bgcolor: active ? '#fff' : 'transparent',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.18s ease',
            }}
        >
            <Typography sx={{
                fontFamily, fontSize: '0.76rem', fontWeight: 600,
                color: active ? colors.textPrimary : colors.textTertiary,
            }}>
                {label}
            </Typography>
            <Box sx={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 18, height: 16, borderRadius: '6px', px: 0.5,
                bgcolor: active ? accent : 'rgba(0,0,0,0.08)',
                color: active ? '#fff' : colors.textTertiary,
                fontFamily, fontSize: '0.58rem', fontWeight: 700,
            }}>
                {count}
            </Box>
        </Box>
    );
}

export default function AgentSpaceAgentDetail({ chatbotId, agentType, onBack, refreshKey }) {
    const { configureAgent, selectAgent } = useAgentSpace();
    const [agentDef, setAgentDef] = useState(null);
    const [loadingDef, setLoadingDef] = useState(true);
    const [connectionStatus, setConnectionStatus] = useState(null);

    const [metrics, setMetrics] = useState(null);
    const [summaries, setSummaries] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [totalTasks, setTotalTasks] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');

    const [drawerSession, setDrawerSession] = useState(null);

    // ---- Load agent def + connection status -----
    useEffect(() => {
        if (!chatbotId || !agentType) return;
        let cancelled = false;
        setLoadingDef(true);
        Promise.all([
            axiosInstance.get('/api/agent-space/agent-types'),
            axiosInstance.get(`/api/agent-space/connections?chatbot_id=${chatbotId}`),
        ])
            .then(([typesRes, connRes]) => {
                if (cancelled) return;
                const def = (typesRes.data?.data || []).find((a) => a.id === agentType);
                setAgentDef(def || null);
                const statuses = connRes.data?.data || {};
                const connKey = def?.connection_type || agentType;
                setConnectionStatus(statuses[connKey] || null);
            })
            .catch(() => { /* fall through */ })
            .finally(() => { if (!cancelled) setLoadingDef(false); });
        return () => { cancelled = true; };
    }, [chatbotId, agentType]);

    const fetchSummaries = useCallback(async (pageNum, append) => {
        if (!chatbotId || !agentType) return;
        if (pageNum === 1) setLoading(true);
        else setLoadingMore(true);
        try {
            const resp = await axiosInstance.get('/api/agent-space/dashboard/task-summaries', {
                params: { chatbot_id: chatbotId, agent_type: agentType, page: pageNum, per_page: 10 },
            });
            if (resp.data?.success) {
                const data = resp.data.data;
                setSummaries((prev) => append ? [...prev, ...data.summaries] : data.summaries);
                setHasMore(data.has_more);
                setPage(data.page);
                setTotalTasks(data.total);
                if (data.agent_metrics) setMetrics(data.agent_metrics);
            }
        } catch (e) {
            console.error('Failed to fetch task summaries:', e);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [chatbotId, agentType]);

    useEffect(() => {
        setSummaries([]);
        setPage(1);
        setHasMore(false);
        fetchSummaries(1, false);
    }, [fetchSummaries, refreshKey]);

    const filteredSummaries = useMemo(() => {
        let rows = summaries;
        if (statusFilter === 'in_progress') {
            rows = rows.filter((t) => t.status === 'partial');
        } else if (statusFilter === 'completed') {
            rows = rows.filter((t) => t.status === 'success');
        } else if (statusFilter === 'failed') {
            rows = rows.filter((t) => t.status === 'failed');
        }
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            rows = rows.filter((t) =>
                (t.title || '').toLowerCase().includes(q)
                || (t.summary || '').toLowerCase().includes(q)
                || (t.key_actions || []).some((a) => (a || '').toLowerCase().includes(q))
            );
        }
        return rows;
    }, [summaries, statusFilter, search]);

    const counts = useMemo(() => ({
        all: summaries.length,
        inProgress: summaries.filter((t) => t.status === 'partial').length,
        completed: summaries.filter((t) => t.status === 'success').length,
        failed: summaries.filter((t) => t.status === 'failed').length,
    }), [summaries]);

    const agentColor = agentDef?.color || colors.accent;
    const isConfigured = agentDef
        ? (agentDef.requires_connection ? !!connectionStatus?.is_connected : true)
        : false;

    const successRate = metrics?.success_rate ?? 0;
    const successColor = successRate >= 80 ? colors.success : successRate >= 50 ? colors.warning : colors.danger;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden', fontFamily, gap: 1.5 }}>
            {/* ---- Header ---- */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 1.5, flexWrap: 'wrap',
                px: { xs: 1.75, sm: 2.5 }, py: 1.5,
                bgcolor: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '16px', flexShrink: 0,
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                    <Tooltip title="Back to agents">
                        <IconButton size="small" onClick={onBack} sx={{
                            color: colors.textSecondary, bgcolor: 'rgba(0,0,0,0.03)',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                        }}>
                            <ArrowBackIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Box sx={{
                        width: 44, height: 44, borderRadius: '12px',
                        background: `linear-gradient(135deg, ${agentColor}22, ${agentColor}10)`,
                        color: agentColor, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: `1px solid ${agentColor}26`,
                    }}>
                        {getIcon(agentDef?.icon_name, 22)}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85 }}>
                            <Typography sx={{
                                fontFamily, fontSize: { xs: '1rem', sm: '1.1rem' },
                                fontWeight: 800, color: colors.textPrimary, letterSpacing: '-0.01em',
                            }} noWrap>
                                {agentDef?.label || agentType}
                            </Typography>
                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.35,
                                px: 0.85, py: 0.2, borderRadius: '6px',
                                bgcolor: isConfigured ? 'rgba(5,150,105,0.1)' : 'rgba(245,158,11,0.1)',
                                border: `1px solid ${isConfigured ? 'rgba(5,150,105,0.22)' : 'rgba(245,158,11,0.22)'}`,
                            }}>
                                <Box sx={{
                                    width: 6, height: 6, borderRadius: '50%',
                                    bgcolor: isConfigured ? colors.success : colors.warning,
                                }} />
                                <Typography sx={{
                                    fontFamily, fontSize: '0.6rem', fontWeight: 700,
                                    color: isConfigured ? colors.success : colors.warning,
                                    textTransform: 'uppercase', letterSpacing: '0.5px',
                                }}>
                                    {isConfigured ? 'Active' : 'Setup needed'}
                                </Typography>
                            </Box>
                        </Box>
                        <Typography sx={{
                            fontFamily, fontSize: '0.74rem', color: colors.textTertiary, mt: 0.2,
                            display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                            {agentDef?.description || 'Agent'}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Tooltip title="Open chat with this agent">
                        <Button
                            size="small"
                            startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: '16px !important' }} />}
                            onClick={() => selectAgent(agentType)}
                            sx={{
                                fontFamily, fontSize: '0.74rem', fontWeight: 700,
                                textTransform: 'none', color: '#fff',
                                background: `linear-gradient(135deg, ${agentColor}, ${agentColor}cc)`,
                                borderRadius: '10px', px: 1.75, py: 0.85,
                                boxShadow: `0 4px 12px ${agentColor}33`,
                                '&:hover': { background: agentColor, opacity: 0.95 },
                            }}
                        >
                            Open chat
                        </Button>
                    </Tooltip>
                    <Tooltip title="Configure this agent">
                        <IconButton
                            size="small"
                            onClick={() => configureAgent(agentType)}
                            sx={{
                                color: colors.textSecondary,
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: '10px',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: colors.textPrimary },
                            }}
                        >
                            <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* ---- Scrollable body ---- */}
            <Box sx={{ flex: 1, overflow: 'auto', pr: 0.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Stat strip */}
                {loadingDef || (loading && !metrics) ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                        {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={86} sx={{ borderRadius: '14px' }} />)}
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                        <StatCard
                            icon={<ForumOutlinedIcon sx={{ fontSize: 15 }} />}
                            label="Conversations"
                            value={metrics?.total_conversations ?? 0}
                            color={agentColor}
                            helper={metrics?.total_conversations
                                ? `Across ${totalTasks} workflow${totalTasks !== 1 ? 's' : ''}`
                                : 'Start a chat to see activity'}
                        />
                        <StatCard
                            icon={<BoltIcon sx={{ fontSize: 15 }} />}
                            label="Actions"
                            value={metrics?.total_actions ?? 0}
                            color={colors.info}
                            helper="Tool calls executed by this agent"
                        />
                        <StatCard
                            icon={<TrendingUpIcon sx={{ fontSize: 15 }} />}
                            label="Success rate"
                            value={successRate}
                            suffix="%"
                            color={successColor}
                            helper={successRate >= 80 ? 'Excellent reliability' : successRate >= 50 ? 'Some failures' : 'Needs attention'}
                        />
                    </Box>
                )}

                {/* ---- Workflows panel ---- */}
                <Box sx={{
                    bgcolor: '#fff',
                    border: '1px solid rgba(0,0,0,0.06)',
                    borderRadius: '16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                    overflow: 'hidden',
                    flexShrink: 0,
                }}>
                    {/* Toolbar: title + subtitle + search + segmented filter */}
                    <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2, pb: 1.5 }}>
                        <Box sx={{
                            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                            gap: 1.5, flexWrap: 'wrap', mb: 1.5,
                        }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{
                                    fontFamily, fontSize: '1rem', fontWeight: 700,
                                    color: colors.textPrimary, letterSpacing: '-0.01em',
                                }}>
                                    Workflows
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.74rem', color: colors.textTertiary, mt: 0.25 }}>
                                    {counts.all} workflow{counts.all !== 1 ? 's' : ''} for this agent
                                    {counts.failed > 0 && (
                                        <Box component="span" sx={{ color: colors.danger, fontWeight: 600 }}> · {counts.failed} failed</Box>
                                    )}
                                </Typography>
                            </Box>

                            {/* Search — custom pill control. Built with a native
                                <input> so the icon + field + clear button live
                                inside ONE border instead of MUI's adornment
                                rendering them as separate boxes. Focus halo
                                uses :focus-within on the wrapper, tinted in
                                the agent's accent color. */}
                             {/* Search — custom pill control. */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 0.85,
                                width: { xs: '100%', sm: 340 },
                                height: 40,
                                px: 1.4,
                                bgcolor: '#fff',
                                border: '1px solid rgba(0,0,0,0.1)',
                                borderRadius: '12px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                transition: 'all 0.18s ease',
                                flexShrink: 0,
                                '&:hover': { borderColor: 'rgba(0,0,0,0.18)' },
                                '&:focus-within': {
                                    borderColor: colors.accent,
                                    boxShadow: `0 0 0 3px ${colors.accent}1f`,
                                },
                            }}>
                                <SearchIcon sx={{
                                    fontSize: 17,
                                    color: search ? colors.accent : colors.textTertiary,
                                    transition: 'color 0.15s ease', flexShrink: 0,
                                }} />
                                <InputBase
                                    placeholder="Search by task, agent, step…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontFamily,
                                        fontSize: '0.8rem',
                                        color: colors.textPrimary,
                                        // Deep targets the inner HTML input to guarantee no native border resets conflict
                                        '& .MuiInputBase-input': {
                                            padding: 0,
                                            border: 0,
                                            outline: 'none',
                                            background: 'transparent',
                                        },
                                        '& .MuiInputBase-input::placeholder': {
                                            color: colors.textTertiary,
                                            opacity: 1,
                                        },
                                    }}
                                />
                                {search && (
                                    <Tooltip title="Clear">
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearch('')}
                                            sx={{
                                                p: 0.4, mr: -0.5,
                                                color: colors.textTertiary,
                                                '&:hover': { color: colors.danger, bgcolor: 'rgba(220,38,38,0.06)' },
                                            }}
                                        >
                                            <ClearIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>

                        {/* Segmented filter */}
                        <Box sx={{
                            display: 'inline-flex', bgcolor: 'rgba(0,0,0,0.04)',
                            borderRadius: '10px', p: '3px',
                        }}>
                            <PillTab active={statusFilter === 'all'} label="All" count={counts.all}
                                onClick={() => setStatusFilter('all')} accent={agentColor} />
                            <PillTab active={statusFilter === 'in_progress'} label="In Progress" count={counts.inProgress}
                                onClick={() => setStatusFilter('in_progress')} accent={colors.warning} />
                            <PillTab active={statusFilter === 'completed'} label="Completed" count={counts.completed}
                                onClick={() => setStatusFilter('completed')} accent={colors.success} />
                            {counts.failed > 0 && (
                                <PillTab active={statusFilter === 'failed'} label="Failed" count={counts.failed}
                                    onClick={() => setStatusFilter('failed')} accent={colors.danger} />
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.05)' }} />

                    {/* Workflow list body */}
                    <Box sx={{ px: { xs: 1, sm: 1.5 }, pt: 1, pb: 1.5 }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 0.5 }}>
                                {[1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={86} sx={{ borderRadius: '12px' }} />)}
                            </Box>
                        ) : filteredSummaries.length === 0 ? (
                            <Box sx={{
                                py: 6, textAlign: 'center', mx: 1,
                                borderRadius: '14px',
                                bgcolor: 'rgba(0,0,0,0.02)',
                                border: '1px dashed rgba(0,0,0,0.08)',
                            }}>
                                <Box sx={{
                                    width: 52, height: 52, borderRadius: '14px',
                                    bgcolor: `${agentColor}10`, color: agentColor,
                                    mx: 'auto', mb: 1.25,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    {getIcon(agentDef?.icon_name, 26)}
                                </Box>
                                <Typography sx={{ fontFamily, fontSize: '0.88rem', fontWeight: 700, color: colors.textPrimary }}>
                                    {search
                                        ? 'No workflows match your search'
                                        : statusFilter !== 'all'
                                            ? `No ${statusFilter.replace('_', ' ')} workflows yet`
                                            : 'No workflows yet'}
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.74rem', color: colors.textTertiary, mt: 0.5, maxWidth: 360, mx: 'auto' }}>
                                    {search
                                        ? 'Try a shorter or different search term.'
                                        : 'Use the chat button (bottom right) to start a task with this agent — it will appear here.'}
                                </Typography>
                                {!search && (
                                    <Button
                                        size="small"
                                        startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: '16px !important' }} />}
                                        onClick={() => selectAgent(agentType)}
                                        sx={{
                                            mt: 1.75, fontFamily, fontSize: '0.74rem', fontWeight: 700,
                                            textTransform: 'none', color: '#fff',
                                            background: `linear-gradient(135deg, ${agentColor}, ${agentColor}cc)`,
                                            borderRadius: '10px', px: 2, py: 0.85,
                                            '&:hover': { background: agentColor, opacity: 0.92 },
                                        }}
                                    >
                                        Start a task
                                    </Button>
                                )}
                            </Box>
                        ) : (
                            <Box sx={{
                                display: 'flex', flexDirection: 'column', gap: 1,
                                p: 0.5,
                            }}>
                                {filteredSummaries.map((t) => {
                                    const isCompleted = t.status === 'success';
                                    const isFailed = t.status === 'failed';
                                    const sc = isCompleted ? colors.success : isFailed ? colors.danger : colors.warning;
                                    const statusBg = isCompleted
                                        ? 'rgba(5,150,105,0.1)'
                                        : isFailed
                                            ? 'rgba(220,38,38,0.1)'
                                            : 'rgba(245,158,11,0.1)';
                                    const statusLabel = isCompleted ? 'Completed' : isFailed ? 'Failed' : 'In Progress';
                                    return (
                                        <Box
                                            key={t.session_code}
                                            onClick={() => setDrawerSession(t.session_code)}
                                            sx={{
                                                position: 'relative',
                                                p: { xs: 1.5, sm: 1.75 },
                                                borderRadius: '14px',
                                                bgcolor: '#fff',
                                                border: '1px solid rgba(0,0,0,0.07)',
                                                cursor: 'pointer',
                                                transition: 'all 0.18s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-1px)',
                                                    borderColor: `${sc}40`,
                                                    boxShadow: `0 6px 18px ${sc}1a`,
                                                    '& .row-chevron': { color: sc, transform: 'translateX(2px)' },
                                                },
                                                // Accent rail in the status color.
                                                '&::before': {
                                                    content: '""', position: 'absolute',
                                                    left: 0, top: 12, bottom: 12,
                                                    width: 3, borderRadius: '0 3px 3px 0',
                                                    bgcolor: sc,
                                                },
                                            }}
                                        >
                                            {/* Top row: status pill + step count + timestamp */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5, ml: 0.5 }}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Box sx={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 0.35,
                                                        px: 0.85, py: 0.25, borderRadius: '6px', bgcolor: statusBg,
                                                    }}>
                                                        {isCompleted && <CheckCircleIcon sx={{ fontSize: 11, color: sc }} />}
                                                        {isFailed && <ErrorIcon sx={{ fontSize: 11, color: sc }} />}
                                                        {!isCompleted && !isFailed && <HourglassEmptyIcon sx={{ fontSize: 11, color: sc }} />}
                                                        <Typography sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 700, color: sc }}>
                                                            {statusLabel}
                                                        </Typography>
                                                    </Box>
                                                    <Typography sx={{
                                                        fontFamily, fontSize: '0.66rem',
                                                        color: colors.textTertiary, fontWeight: 600,
                                                    }}>
                                                        {t.steps_count} step{t.steps_count !== 1 ? 's' : ''}
                                                        {t.success_count > 0 && (
                                                            <Box component="span" sx={{ ml: 0.5, color: colors.success }}>
                                                                · {t.success_count} ok
                                                            </Box>
                                                        )}
                                                        {t.failed_count > 0 && (
                                                            <Box component="span" sx={{ ml: 0.5, color: colors.danger }}>
                                                                · {t.failed_count} failed
                                                            </Box>
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{
                                                    fontFamily, fontSize: '0.66rem',
                                                    color: colors.textTertiary, whiteSpace: 'nowrap',
                                                }}>
                                                    {formatRelative(t.updated_at || t.created_at)}
                                                </Typography>
                                            </Box>

                                            {/* Title */}
                                            <Typography sx={{
                                                fontFamily, fontSize: '0.92rem', fontWeight: 700,
                                                color: colors.textPrimary, lineHeight: 1.35, mb: 0.5, ml: 0.5,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                            }}>
                                                {t.title || 'Untitled workflow'}
                                            </Typography>

                                            {/* Summary */}
                                            {t.summary && (
                                                <Typography sx={{
                                                    fontFamily, fontSize: '0.74rem', color: colors.textSecondary,
                                                    lineHeight: 1.55, ml: 0.5,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                }}>
                                                    {t.summary}
                                                </Typography>
                                            )}

                                            {/* Action chips */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.85, ml: 0.5 }}>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, minWidth: 0 }}>
                                                    {(t.key_actions || []).slice(0, 4).map((action, i) => (
                                                        <Chip
                                                            key={i}
                                                            label={action}
                                                            size="small"
                                                            sx={{
                                                                fontFamily, fontSize: '0.58rem', height: 18,
                                                                fontWeight: 600,
                                                                bgcolor: `${agentColor}10`,
                                                                color: agentColor,
                                                                border: `1px solid ${agentColor}24`,
                                                            }}
                                                        />
                                                    ))}
                                                    {t.key_actions && t.key_actions.length > 4 && (
                                                        <Typography sx={{ fontFamily, fontSize: '0.6rem', color: colors.textTertiary, alignSelf: 'center', ml: 0.25 }}>
                                                            +{t.key_actions.length - 4}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <ChevronRightIcon
                                                    className="row-chevron"
                                                    sx={{
                                                        fontSize: 18, color: colors.textTertiary,
                                                        transition: 'all 0.15s ease', flexShrink: 0,
                                                    }}
                                                />
                                            </Box>

                                            {/* Attachments — files spilled to GCS by
                                                the engine during this workflow (e.g.
                                                large spreadsheet dumps, exports). Each
                                                chip opens the file in a new tab. */}
                                            {t.files && t.files.length > 0 && (
                                                <Box sx={{
                                                    mt: 1, pt: 0.85, ml: 0.5,
                                                    borderTop: '1px dashed rgba(0,0,0,0.08)',
                                                    display: 'flex', flexDirection: 'column', gap: 0.45,
                                                }}>
                                                    <Typography sx={{
                                                        fontFamily, fontSize: '0.56rem', fontWeight: 700,
                                                        color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.4px',
                                                    }}>
                                                        Attachments · {t.files.length}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {t.files.map((f) => (
                                                            <Box
                                                                key={f.id}
                                                                component="a"
                                                                href={f.file_url || '#'}
                                                                target="_blank"
                                                                rel="noopener"
                                                                onClick={(e) => e.stopPropagation()}
                                                                sx={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 0.45,
                                                                    px: 0.85, py: 0.3, borderRadius: '7px',
                                                                    textDecoration: 'none',
                                                                    bgcolor: `${agentColor}10`,
                                                                    border: `1px solid ${agentColor}26`,
                                                                    color: agentColor,
                                                                    '&:hover': {
                                                                        bgcolor: `${agentColor}1a`,
                                                                        borderColor: `${agentColor}55`,
                                                                    },
                                                                }}
                                                            >
                                                                <InsertDriveFileOutlinedIcon sx={{ fontSize: 12 }} />
                                                                <Typography sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 600 }} noWrap>
                                                                    {f.filename || f.label || 'File'}
                                                                </Typography>
                                                                {f.file_type && (
                                                                    <Typography sx={{ fontFamily, fontSize: '0.55rem', color: `${agentColor}cc`, fontWeight: 600 }}>
                                                                        · {f.file_type.toUpperCase()}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>

                    {/* Load more */}
                    {hasMore && !loading && (
                        <Box sx={{
                            display: 'flex', justifyContent: 'center', py: 1.5,
                            borderTop: '1px solid rgba(0,0,0,0.04)',
                        }}>
                            <Button
                                onClick={() => fetchSummaries(page + 1, true)}
                                disabled={loadingMore}
                                size="small"
                                startIcon={<KeyboardArrowDownIcon sx={{ fontSize: '16px !important' }} />}
                                sx={{
                                    fontFamily, fontSize: '0.74rem', fontWeight: 600,
                                    textTransform: 'none', color: agentColor,
                                    '&:hover': { bgcolor: `${agentColor}10` },
                                    borderRadius: '8px', px: 1.5,
                                }}
                            >
                                {loadingMore ? 'Loading…' : `Load more · showing ${summaries.length} of ${totalTasks}`}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Slide-in drawer with step-by-step task detail */}
            <Drawer
                anchor="right"
                open={!!drawerSession}
                onClose={() => setDrawerSession(null)}
                PaperProps={{ sx: { width: { xs: '100vw', sm: 540 }, borderRadius: { xs: 0, sm: '14px 0 0 14px' } } }}
            >
                {drawerSession && (
                    <AgentSpaceTaskDetail
                        activity={{
                            agent_type: agentType,
                            resource_name: summaries.find((s) => s.session_code === drawerSession)?.title || 'Workflow',
                            status: summaries.find((s) => s.session_code === drawerSession)?.status || 'partial',
                            timestamp: summaries.find((s) => s.session_code === drawerSession)?.created_at,
                            metadata: { session_code: drawerSession },
                        }}
                        chatbotId={chatbotId}
                        onBack={() => setDrawerSession(null)}
                    />
                )}
            </Drawer>
        </Box>
    );
}
